import React, { useEffect, useState, useRef, useCallback } from 'react';
import { masterApi } from '../api/masterApi';

const PAGE_SIZE = 100;

const CustomerInfoPage = () => {
  const [items, setItems] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [savingDealer, setSavingDealer] = useState(null);

  const offsetRef = useRef(0);
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const loadGenRef = useRef(0);
  const keywordRef = useRef(keyword);
  const stateRef = useRef({ loadingMore: false, hasMore: true, loading: false });
  const initialLoadCompletedRef = useRef(false);

  useEffect(() => {
    keywordRef.current = keyword;
  }, [keyword]);

  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading };
  }, [loadingMore, hasMore, loading]);

  const formatCustomerDisp = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const loadInitial = useCallback(async (keywordOverride) => {
    const kw =
      keywordOverride !== undefined ? keywordOverride : keyword;
    const kwTrimmed = typeof kw === 'string' ? kw.trim() : '';
    loadGenRef.current += 1;
    const gen = loadGenRef.current;

    try {
      setLoading(true);
      offsetRef.current = 0;
      const data = await masterApi.getCustomerInfo(
        kwTrimmed || undefined,
        PAGE_SIZE,
        0
      );
      if (gen !== loadGenRef.current) return;

      setItems(data);
      offsetRef.current = data.length;
      setHasMore(data.length === PAGE_SIZE);
      initialLoadCompletedRef.current = true;
      setTimeout(() => {
        initialLoadCompletedRef.current = false;
      }, 500);
    } catch (e) {
      console.error('顧客情報取得エラー:', e);
      alert('顧客情報の取得に失敗しました');
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false);
      }
    }
  }, [keyword]);

  useEffect(() => {
    loadInitial();
    // 初回のみ。検索はフォーム submit、キーワード入力では再取得しない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreItems = async () => {
    const currentState = stateRef.current;
    const gen = loadGenRef.current;
    if (
      initialLoadCompletedRef.current ||
      currentState.loadingMore ||
      !currentState.hasMore ||
      currentState.loading
    ) {
      return;
    }

    try {
      setLoadingMore(true);
      const kwTrimmed = keywordRef.current.trim();
      const currentOffset = offsetRef.current;
      const data = await masterApi.getCustomerInfo(
        kwTrimmed || undefined,
        PAGE_SIZE,
        currentOffset
      );
      if (gen !== loadGenRef.current) return;

      setItems((prev) => [...prev, ...data]);
      offsetRef.current = currentOffset + data.length;
      setHasMore(data.length === PAGE_SIZE);
    } catch (e) {
      console.error('顧客情報追加取得エラー:', e);
      alert('追加の取得に失敗しました');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          const currentState = stateRef.current;
          if (
            entry.isIntersecting &&
            currentState.hasMore &&
            !currentState.loadingMore &&
            !currentState.loading &&
            !initialLoadCompletedRef.current
          ) {
            loadMoreItems();
          }
        },
        {
          threshold: 0,
          rootMargin: '500px',
        }
      );
    }

    const observer = observerRef.current;

    const setupObserver = () => {
      const target = observerTarget.current;
      if (target) {
        const isObserving = target.getAttribute('data-observing') === 'true';
        if (isObserving) {
          observer.unobserve(target);
          target.removeAttribute('data-observing');
        }
        const timeoutId = setTimeout(() => {
          const currentTarget = observerTarget.current;
          if (currentTarget && !currentTarget.getAttribute('data-observing')) {
            observer.observe(currentTarget);
            currentTarget.setAttribute('data-observing', 'true');
          }
        }, 50);
        return { timeoutId };
      }
      return null;
    };

    let setupResult = setupObserver();
    let timeoutId1 = null;
    let timeoutId2 = null;
    let timeoutId3 = null;

    if (!setupResult) {
      timeoutId1 = setTimeout(() => {
        setupResult = setupObserver();
        if (!setupResult) {
          timeoutId2 = setTimeout(() => {
            setupResult = setupObserver();
            if (!setupResult) {
              timeoutId3 = setTimeout(() => {
                setupObserver();
              }, 300);
            }
          }, 300);
        }
      }, 300);
    }

    return () => {
      if (setupResult?.timeoutId) clearTimeout(setupResult.timeoutId);
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      if (timeoutId3) clearTimeout(timeoutId3);
      const target = observerTarget.current;
      if (target && target.hasAttribute('data-observing')) {
        observer.unobserve(target);
        target.removeAttribute('data-observing');
      }
    };
  }, [hasMore, items.length]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadInitial();
  };

  const handleFieldChange = (dealerCode, field, value) => {
    setItems((prev) =>
      prev.map((row) =>
        row.dealer_code === dealerCode ? { ...row, [field]: value } : row
      )
    );
  };

  const handleSave = async (row) => {
    try {
      setSavingDealer(row.dealer_code);
      let arrivalLeadTime = null;
      if (
        row.arrival_lead_time !== null &&
        row.arrival_lead_time !== '' &&
        row.arrival_lead_time !== undefined
      ) {
        const numValue = Number(row.arrival_lead_time);
        if (!isNaN(numValue) && numValue > 0) {
          arrivalLeadTime = numValue;
        }
      }

      const payload = {
        arrival_lead_time: arrivalLeadTime,
        memo: row.memo && row.memo.trim() ? row.memo.trim() : null,
        reason: '顧客情報画面から更新',
      };
      const res = await masterApi.updateCustomerInfo(row.dealer_code, payload);
      setItems((prev) =>
        prev.map((r) =>
          r.dealer_code === res.dealer_code
            ? {
                ...r,
                arrival_lead_time: res.arrival_lead_time,
                memo: res.memo,
              }
            : r
        )
      );
      alert('保存しました');
    } catch (e) {
      console.error('顧客情報更新エラー:', e);
      alert('保存に失敗しました');
    } finally {
      setSavingDealer(null);
    }
  };

  const showEmptyState = !loading && items.length === 0;
  const showOverlay = loading && items.length > 0;

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="p-6 border-b border-slate-200 bg-white compact:px-4 compact:py-3">
        <h1 className="page-title">顧客情報</h1>
      </div>

      <div className="px-6 py-3 border-b border-slate-200 bg-white compact:px-4 compact:py-2">
        <form onSubmit={handleSearch} className="filter-toolbar">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">検索</label>
            <input
              type="text"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm w-64"
              placeholder="得意先コード / 得意先名"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-toolbar bg-slate-700 text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-50"
          >
            検索
          </button>
          <button
            type="button"
            onClick={() => {
              setKeyword('');
              loadInitial('');
            }}
            disabled={loading}
            className="btn-toolbar border border-slate-300 text-sm rounded-md bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            クリア
          </button>
        </form>
      </div>

      {/* 縦スクロールはテーブル直下の overflow-auto に寄せる（その中で thead sticky が効く） */}
      <div className="flex-1 flex flex-col min-h-0 p-6">
        <div className="flex-1 flex flex-col min-h-0 bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden relative">
          {showOverlay && (
            <div className="absolute inset-0 bg-white/60 z-20 flex items-start justify-center pt-6 pointer-events-none">
              <span className="text-sm text-slate-600">読み込み中…</span>
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_rgb(226_232_240)]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">
                    得意先コード
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">
                    得意先名
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">
                    着日LT（日）
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">
                    備考
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {loading && items.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-12 text-center text-sm text-slate-500"
                    >
                      読み込み中です…
                    </td>
                  </tr>
                )}
                {showEmptyState && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-sm text-slate-500"
                    >
                      データがありません
                    </td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr key={row.dealer_code}>
                    <td className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
                      {formatCustomerDisp(row.customer_disp ?? row.dealer_code)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">
                      {row.dealer_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">
                      <input
                        type="number"
                        className="w-24 px-2 py-1 border border-slate-300 rounded-md text-sm"
                        value={
                          row.arrival_lead_time === null ||
                          row.arrival_lead_time === undefined
                            ? ''
                            : row.arrival_lead_time
                        }
                        onChange={(e) =>
                          handleFieldChange(
                            row.dealer_code,
                            'arrival_lead_time',
                            e.target.value === '' ? '' : Number(e.target.value)
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-slate-300 rounded-md text-sm"
                        value={row.memo ?? ''}
                        onChange={(e) =>
                          handleFieldChange(
                            row.dealer_code,
                            'memo',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleSave(row)}
                        disabled={savingDealer === row.dealer_code}
                        className="px-3 py-1 rounded-md text-sm bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {savingDealer === row.dealer_code ? '保存中…' : '保存'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && items.length > 0 && (
              <div
                ref={observerTarget}
                className="h-1 flex-shrink-0 bg-transparent"
                style={{ minHeight: '1px' }}
              />
            )}
          </div>
        </div>
      </div>
      {loadingMore && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg shadow-lg">
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm font-medium">追加データを読み込み中...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerInfoPage;
